import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { socketService } from "../../../services/socket";

interface AuditEntry {
  id: string;
  user_id: string | null;
  user_email: string | null;
  changed_by: string | null;
  changed_by_email: string | null;
  action: "create" | "update" | "delete" | "bulk_update";
  old_role: string | null;
  new_role: string | null;
  old_project_id: string | null;
  new_project_id: string | null;
  changed_at: string;
}

interface AuditResponse {
  success: boolean;
  entries?: AuditEntry[];
  total?: number;
  limit?: number;
  offset?: number;
  error?: string;
}

const PAGE_SIZE = 50;

const ACTION_COLOR: Record<AuditEntry["action"], string> = {
  create: "#10B981",
  update: "#38BDF8",
  bulk_update: "#A3E635",
  delete: "#f72d60",
};

const ROLE_COLOR: Record<string, string> = {
  Logger: "var(--color-data-log)",
  "Story Producer": "var(--color-data-story)",
  "Content Director": "var(--color-role-contentdirector)",
  Showrunner: "var(--color-role-showrunner)",
  "Content Controller": "var(--color-data-event)",
  Admin: "var(--color-data-transcript)",
  Superadmin: "var(--color-accent-danger)",
};

const projectLabel = (id: string | null, projectsById: Record<string, string>) => {
  if (!id) return "—";
  return projectsById[id] || id.slice(0, 8);
};

const renderRoleCell = (oldRole: string | null, newRole: string | null) => {
  if (oldRole === newRole) return <span style={{ color: "var(--color-text-muted)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: ROLE_COLOR[oldRole || ""] || "var(--color-text-muted)" }}>
        {oldRole || "∅"}
      </span>
      <i className="bx bx-right-arrow-alt" style={{ color: "var(--color-text-muted)" }}></i>
      <span style={{ color: ROLE_COLOR[newRole || ""] || "var(--color-text-muted)" }}>
        {newRole || "∅"}
      </span>
    </span>
  );
};

const renderProjectCell = (
  oldId: string | null,
  newId: string | null,
  projectsById: Record<string, string>,
) => {
  if (oldId === newId) return <span style={{ color: "var(--color-text-muted)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span>{projectLabel(oldId, projectsById)}</span>
      <i className="bx bx-right-arrow-alt" style={{ color: "var(--color-text-muted)" }}></i>
      <span>{projectLabel(newId, projectsById)}</span>
    </span>
  );
};

export const AuditLogs: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>("");
  const [projectsById, setProjectsById] = useState<Record<string, string>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const activeProjectId = searchParams.get("projectId");

  const load = useCallback((opts: { offset: number; action?: string; projectId?: string }) => {
    setIsLoading(true);
    setError(null);
    socketService.emit("admin:get-role-audit", {
      offset: opts.offset,
      limit: PAGE_SIZE,
      ...(opts.action ? { action: opts.action } : {}),
      ...(opts.projectId ? { projectId: opts.projectId } : {}),
    });
  }, []);

  useEffect(() => {
    const handler = (data: AuditResponse) => {
      setIsLoading(false);
      if (!data.success) {
        setError(data.error || "Error loading audit log");
        return;
      }
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setOffset(data.offset || 0);
    };
    const usersHandler = (data: any) => {
      if (data?.projects) {
        const map: Record<string, string> = {};
        for (const p of data.projects) map[p.id] = p.name;
        setProjectsById(map);
      }
    };
    socketService.on("admin:role-audit-data", handler);
    socketService.on("admin:users-data", usersHandler);
    // Pull projects list so we can render names instead of UUIDs.
    socketService.emit("admin:get-users");
    load({ offset: 0, action: actionFilter || undefined, projectId: activeProjectId || undefined });
    return () => {
      socketService.off("admin:role-audit-data", handler);
      socketService.off("admin:users-data", usersHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () =>
    load({ offset, action: actionFilter || undefined, projectId: activeProjectId || undefined });
  const applyFilter = (v: string) => {
    setActionFilter(v);
    load({ offset: 0, action: v || undefined, projectId: activeProjectId || undefined });
  };
  const goPrev = () =>
    load({
      offset: Math.max(0, offset - PAGE_SIZE),
      action: actionFilter || undefined,
      projectId: activeProjectId || undefined,
    });
  const goNext = () =>
    load({
      offset: offset + PAGE_SIZE,
      action: actionFilter || undefined,
      projectId: activeProjectId || undefined,
    });

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + entries.length, total);

  return (
    <div
      data-testid="admin-view-audit"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        {activeProjectId && (
          <button
            onClick={() => setSearchParams({ view: "projects" })}
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "var(--color-text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              borderRadius: "8px",
              transition: "all 0.2s",
            }}
            title="Back to projects"
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
              e.currentTarget.style.color = "var(--color-text-secondary)";
            }}
          >
            <i className="bx bx-arrow-back" style={{ fontSize: "1.2rem" }}></i>
          </button>
        )}
        <label style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>Acción:</label>
        <select
          value={actionFilter}
          onChange={(e) => applyFilter(e.target.value)}
          data-testid="audit-filter-action"
          style={{
            background: "#111",
            border: "1px solid #333",
            color: "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: "0.8rem",
          }}
        >
          <option value="">Todas</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="bulk_update">Bulk update</option>
          <option value="delete">Delete</option>
        </select>
        <button
          onClick={refresh}
          data-testid="audit-refresh"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "0.8rem",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="bx bx-refresh"></i> Refrescar
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
          {pageStart}–{pageEnd} de {total}
        </div>
        <button
          onClick={goPrev}
          disabled={offset === 0 || isLoading}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: offset === 0 ? "#555" : "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: offset === 0 || isLoading ? "not-allowed" : "pointer",
            fontSize: "0.8rem",
          }}
        >
          <i className="bx bx-chevron-left"></i>
        </button>
        <button
          onClick={goNext}
          disabled={offset + entries.length >= total || isLoading}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: offset + entries.length >= total ? "#555" : "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: offset + entries.length >= total || isLoading ? "not-allowed" : "pointer",
            fontSize: "0.8rem",
          }}
        >
          <i className="bx bx-chevron-right"></i>
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          border: "1px solid var(--color-panel-border)",
          borderRadius: 12,
          background: "var(--color-panel-bg)",
        }}
      >
        {error && (
          <div
            style={{
              padding: 16,
              color: "var(--color-accent-danger)",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </div>
        )}
        {!error && isLoading && entries.length === 0 && (
          <div
            style={{
              padding: 24,
              color: "var(--color-text-muted)",
              fontSize: "0.85rem",
              textAlign: "center",
            }}
          >
            Cargando…
          </div>
        )}
        {!error && !isLoading && entries.length === 0 && (
          <div
            style={{
              padding: 24,
              color: "var(--color-text-muted)",
              fontSize: "0.85rem",
              textAlign: "center",
            }}
          >
            Sin registros.
          </div>
        )}
        {entries.length > 0 && (
          <table
            data-testid="audit-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.8rem",
              color: "var(--color-text-primary)",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "rgba(255,255,255,0.04)",
                zIndex: 1,
              }}
            >
              <tr>
                <Th>Fecha</Th>
                <Th>Acción</Th>
                <Th>Usuario afectado</Th>
                <Th>Rol (antes → después)</Th>
                <Th>Proyecto (antes → después)</Th>
                <Th>Modificado por</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                  <Td>{new Date(e.changed_at).toLocaleString()}</Td>
                  <Td>
                    <span
                      style={{
                        background: `${ACTION_COLOR[e.action]}22`,
                        color: ACTION_COLOR[e.action],
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {e.action}
                    </span>
                  </Td>
                  <Td>{e.user_email || e.user_id?.slice(0, 8) || "—"}</Td>
                  <Td>{renderRoleCell(e.old_role, e.new_role)}</Td>
                  <Td>{renderProjectCell(e.old_project_id, e.new_project_id, projectsById)}</Td>
                  <Td>{e.changed_by_email || e.changed_by?.slice(0, 8) || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    style={{
      textAlign: "left",
      padding: "10px 12px",
      fontWeight: 700,
      fontSize: "0.72rem",
      letterSpacing: "0.5px",
      color: "var(--color-text-muted)",
      textTransform: "uppercase",
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>
);
